import { computed, ref } from 'vue'
import type { Mission, BaseMissionElement } from '@/types/route.types'

/**
 * Singleton state for mission list and selection.
 * Shared across the app; no persistence (missions are fetched from API when needed).
 * Uses ref() (not shallowRef) so in-place edits (e.g. isEditable, isVisible) trigger reactivity.
 */
const missions = ref<Mission[]>([])
const activeMissionIndex = ref<number | null>(0)
const activeFlightRoute = ref<BaseMissionElement | null>(null)

export function useMissionsState() {
  const activeMission = computed(() => {
    if (activeMissionIndex.value === null) return null
    return missions.value[activeMissionIndex.value] ?? null
  })

  const hasUnsavedChanges = computed(() =>
    missions.value.some((route) => route.hasUnsavedChanges === true),
  )

  function setMissionSyncingState(missionId: string, state: boolean | null) {
    const routeIndex = missions.value.findIndex((route) => route.missionId === missionId)
    if (routeIndex >= 0 && routeIndex < missions.value.length) {
      missions.value[routeIndex].isSyncing = state
    }
  }

  function setMissionHasUnsavedChanges(missionId: string, value: boolean) {
    const routeIndex = missions.value.findIndex((route) => route.missionId === missionId)
    if (routeIndex >= 0 && routeIndex < missions.value.length) {
      missions.value[routeIndex].hasUnsavedChanges = value
    }
  }

  return {
    missions,
    activeMissionIndex,
    activeFlightRoute,
    activeMission,
    hasUnsavedChanges,
    setMissionSyncingState,
    setMissionHasUnsavedChanges,
  }
}
